import React, { useState, useRef, useCallback } from "react";

const SEGMENTS = [
  { label: "10% Descuento", weight: 30, color: "#e07020" },
  { label: "Consulta Gratis", weight: 25, color: "#1a1a1a" },
  { label: "Piercing Gratis", weight: 5, color: "#e07020" },
  { label: "15% Descuento", weight: 20, color: "#1a1a1a" },
  { label: "Sticker Gratis", weight: 15, color: "#e07020" },
  { label: "20% Descuento", weight: 5, color: "#1a1a1a" },
];

function weightedRandom(segments) {
  const total = segments.reduce((sum, s) => sum + s.weight, 0);
  let r = Math.random() * total;
  for (let i = 0; i < segments.length; i++) {
    r -= segments[i].weight;
    if (r <= 0) return i;
  }
  return segments.length - 1;
}

function drawWheel(canvas, segments, rotation) {
  const ctx = canvas.getContext("2d");
  const cx = canvas.width / 2;
  const cy = canvas.height / 2;
  const radius = Math.min(cx, cy) - 8;
  const total = segments.reduce((sum, s) => sum + s.weight, 0);

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotation);

  let startAngle = 0;
  segments.forEach((seg, i) => {
    const sliceAngle = (seg.weight / total) * 2 * Math.PI;

    // Draw segment
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, radius, startAngle, startAngle + sliceAngle);
    ctx.closePath();
    ctx.fillStyle = seg.color;
    ctx.fill();
    ctx.strokeStyle = "#333";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw text
    ctx.save();
    ctx.rotate(startAngle + sliceAngle / 2);
    ctx.textAlign = "right";
    ctx.fillStyle = "#fff";
    ctx.font = `bold ${radius < 180 ? 13 : 16}px 'Segoe UI', Arial, sans-serif`;
    ctx.shadowColor = "rgba(0,0,0,0.7)";
    ctx.shadowBlur = 4;
    ctx.fillText(seg.label, radius - 20, 5);
    ctx.shadowBlur = 0;
    ctx.restore();

    startAngle += sliceAngle;
  });

  ctx.restore();

  // Outer ring
  ctx.beginPath();
  ctx.arc(cx, cy, radius + 4, 0, 2 * Math.PI);
  ctx.strokeStyle = "#e07020";
  ctx.lineWidth = 4;
  ctx.stroke();

  // Center circle
  ctx.beginPath();
  ctx.arc(cx, cy, 22, 0, 2 * Math.PI);
  ctx.fillStyle = "#e07020";
  ctx.fill();
  ctx.strokeStyle = "#0A0A0A";
  ctx.lineWidth = 3;
  ctx.stroke();
}

function drawPointer(canvas) {
  const ctx = canvas.getContext("2d");
  const cx = canvas.width / 2;
  ctx.save();
  ctx.fillStyle = "#e07020";
  ctx.strokeStyle = "#0A0A0A";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx - 15, 10);
  ctx.lineTo(cx + 15, 10);
  ctx.lineTo(cx, 40);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

export default function Ruleta() {
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const canvasRef = useRef(null);
  const pointerCanvasRef = useRef(null);
  const rotationRef = useRef(0);
  const animRef = useRef(null);

  const canvasSize = 380;

  const initCanvas = useCallback(
    (node) => {
      canvasRef.current = node;
      if (node) {
        node.width = canvasSize;
        node.height = canvasSize;
        drawWheel(node, SEGMENTS, rotationRef.current);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const initPointer = useCallback((node) => {
    pointerCanvasRef.current = node;
    if (node) {
      node.width = canvasSize;
      node.height = 50;
      drawPointer(node);
    }
  }, []);

  const spin = () => {
    if (spinning) return;
    setSpinning(true);
    setShowModal(false);
    setResult(null);

    const winnerIndex = weightedRandom(SEGMENTS);
    const total = SEGMENTS.reduce((sum, s) => sum + s.weight, 0);

    // Calculate the angle where the winner segment center is
    let angleToWinner = 0;
    for (let i = 0; i < winnerIndex; i++) {
      angleToWinner += (SEGMENTS[i].weight / total) * 2 * Math.PI;
    }
    angleToWinner += (SEGMENTS[winnerIndex].weight / total) * Math.PI; // center of segment

    // The pointer is at top (angle = -PI/2 relative to canvas)
    // We need the segment to end up under the pointer
    // Pointer is at the top = angle -PI/2 in standard coords
    // After rotation R, the segment at angle A ends up at angle (A + R)
    // We want A + R = -PI/2 (mod 2PI) => R = -PI/2 - A
    const fullSpins = 5 + Math.floor(Math.random() * 5); // 5-9 full rotations
    const targetRotation =
      fullSpins * 2 * Math.PI + (-Math.PI / 2 - angleToWinner);

    const startRotation = rotationRef.current;
    const totalDelta = targetRotation - startRotation;
    const duration = 4000 + Math.random() * 1000;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      // Cubic ease-out for smooth deceleration
      const eased = 1 - Math.pow(1 - t, 3);
      const currentRotation = startRotation + totalDelta * eased;
      rotationRef.current = currentRotation;

      if (canvasRef.current) {
        drawWheel(canvasRef.current, SEGMENTS, currentRotation);
      }

      if (t < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        // Normalize rotation
        rotationRef.current =
          currentRotation % (2 * Math.PI);
        setSpinning(false);
        setResult(SEGMENTS[winnerIndex]);
        setShowModal(true);
      }
    };

    if (animRef.current) cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(animate);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0A0A0A",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Segoe UI', Arial, sans-serif",
        padding: "20px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Branding */}
      <h1
        style={{
          color: "#e07020",
          fontSize: "clamp(28px, 5vw, 42px)",
          fontWeight: 800,
          letterSpacing: "3px",
          textTransform: "uppercase",
          marginBottom: "4px",
        }}
      >
        Bendito Tattoo
      </h1>
      <p
        style={{
          color: "#888",
          fontSize: "14px",
          letterSpacing: "6px",
          textTransform: "uppercase",
          marginBottom: "30px",
        }}
      >
        Gira y Gana
      </p>

      {/* Wheel container */}
      <div
        style={{
          position: "relative",
          width: canvasSize,
          height: canvasSize + 20,
        }}
      >
        {/* Pointer canvas (on top) */}
        <canvas
          ref={initPointer}
          style={{
            position: "absolute",
            top: -20,
            left: 0,
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
        {/* Wheel canvas */}
        <canvas
          ref={initCanvas}
          style={{
            position: "absolute",
            top: 20,
            left: 0,
            zIndex: 1,
          }}
        />
      </div>

      {/* Spin button */}
      <button
        onClick={spin}
        disabled={spinning}
        style={{
          marginTop: "20px",
          padding: "14px 48px",
          fontSize: "20px",
          fontWeight: 700,
          letterSpacing: "4px",
          textTransform: "uppercase",
          color: "#0A0A0A",
          background: spinning
            ? "#555"
            : "linear-gradient(135deg, #e07020, #f0a050)",
          border: "none",
          borderRadius: "50px",
          cursor: spinning ? "not-allowed" : "pointer",
          transition: "all 0.3s ease",
          boxShadow: spinning
            ? "none"
            : "0 4px 20px rgba(224, 112, 32, 0.4)",
        }}
      >
        {spinning ? "..." : "Girar"}
      </button>

      {/* Result modal */}
      {showModal && result && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: "#1a1a1a",
              border: "2px solid #e07020",
              borderRadius: "16px",
              padding: "40px 50px",
              textAlign: "center",
              maxWidth: "90vw",
              animation: "popIn 0.3s ease",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p
              style={{
                color: "#888",
                fontSize: "16px",
                textTransform: "uppercase",
                letterSpacing: "3px",
                marginBottom: "12px",
              }}
            >
              Ganaste
            </p>
            <h2
              style={{
                color: "#e07020",
                fontSize: "clamp(24px, 5vw, 36px)",
                fontWeight: 800,
                marginBottom: "24px",
              }}
            >
              {result.label}
            </h2>
            <button
              onClick={() => setShowModal(false)}
              style={{
                padding: "10px 36px",
                fontSize: "14px",
                fontWeight: 600,
                color: "#fff",
                background: "transparent",
                border: "1px solid #e07020",
                borderRadius: "50px",
                cursor: "pointer",
                letterSpacing: "2px",
                textTransform: "uppercase",
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Keyframe animation for modal */}
      <style>{`
        @keyframes popIn {
          0% { transform: scale(0.7); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
